import { useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { get } from 'lodash-es';
import * as yup from 'yup';
import { Navigate, useLocation } from 'react-router';
import { ROUTES } from '../../../../../constants/RouteNameMapping';
import { MainContainerWithAlert } from '../../components/commonStyledComponents';
import { isRunnableScenario } from '../../utils/scenarioUtils';
import { useAllScenariosLocation } from '../../hooks/useAllScenariosLocation';
import { useToastMessageState } from '../../../../../components/ToastMessage/ToastMessageProvider';
import { currentScenarioStateJ } from '../../../../../services/state/ScenariosStateJ';
import { SubSection } from '../../../Summary/components/SubSection';
import {
  IScenarioRunDataModel,
  IScenarioRunResponseDataModel,
} from '../../../../../data-models/scenario-run.data-model';
import { runScenario } from '../../../../../services/queries/MaggieScenarioQueries';
import { Loader } from '../../../../../components/Loader/Loader';
import { IScenarioDataModel, ScenarioTransactionType } from '../../../../../data-models/scenario.data-model';
import { scenarioRunFieldsSchema } from '../../../../../schemas/ScenarioRun.schema';
import { schemaToFormFields, useSchemaToGrid } from '../../../../../util/schema-utils';
import { IFormField } from '../../../../../view-models/form.view-model';
import { NoDataMessage2 } from '../../../components/Messages/HelpMessage';
import { VisualizationHeading } from './VisualizationHeading';
import { ScenarioKPIs } from './ScenarioKPIs';
import { ScenarioCharts } from './ScenarioCharts';
import { ScenarioRunByFund } from './ScenarioRunByFund';

export type ScenarioTypeByTransactions =
  'roundOnly' | 'exitOnly' | 'partialExit' | 'roundAndExit' | 'roundAndPartialExit';

// Cashless warrants have no cash cost basis, so their MOIC is measured against the notional exercise cost.
export const NOTIONAL_WARRANT_MOIC_NOTE =
  'As-if-exercise-cost multiple: net distribution ÷ (strike × exercised shares), not a cash-basis MOIC.';

export function scenarioVisualizationSchemaRoundOnly() {
  return scenarioRunFieldsSchema().pick(['grossMoic', 'fmv', 'postMoneyValuation', 'ownershipPercentage']);
}
export function scenarioVisualizationSchemaExitOnly() {
  return scenarioRunFieldsSchema().pick(['grossMoic', 'grossProceeds', 'ownershipPercentage']);
}
export function scenarioVisualizationSchemaPartialExit() {
  return scenarioRunFieldsSchema().pick(['grossMoic', 'grossProceeds', 'partialNumberOfSharesPercentage']);
}
export function scenarioVisualizationSchemaRoundAndExit() {
  return scenarioRunFieldsSchema().pick([
    'grossMoic',
    'grossProceeds',
    'postMoneyValuation',
    'ownershipPercentage',
  ]);
}
export function scenarioVisualizationSchemaRoundOnlyGrid() {
  return scenarioRunFieldsSchema().pick([
    'name',
    'seniority',
    'pricePerShare',
    'investedAmount',
    'fmv',
    'grossMoic',
    'moicCostBasis',
    'numberOfSharesOwned',
    'fullyDilutedShares',
    'ownershipPercentage',
  ]);
}
export function scenarioVisualizationSchemaRoundAndPartialExitGrid() {
  return scenarioRunFieldsSchema().pick([
    'name',
    'pricePerShare',
    'impliedPricePerShare',
    'partialInvestedAmount',
    'grossProceeds',
    'grossMoic',
    'moicCostBasis',
    'partialNumberOfShares',
    'partialNumberOfSharesPercentage',
    'remainingNumberOfSharesHeld',
    'fullyDilutedShares',
  ]);
}
export function scenarioVisualizationSchemaPartialExitOnlyGrid() {
  return scenarioVisualizationSchemaRoundAndPartialExitGrid();
}
export function scenarioVisualizationSchemaRoundAndExitGrid() {
  return scenarioRunFieldsSchema().pick([
    'name',
    'seniority',
    'pricePerShare',
    'impliedPricePerShare',
    'investedAmount',
    'liquidationProceeds',
    'commonProceeds',
    'cappedProceeds',
    'grossProceeds',
    'grossMoic',
    'moicCostBasis',
    'numberOfSharesOwned',
    'fullyDilutedShares',
    'ownershipPercentage',
  ]);
}
export function scenarioVisualizationSchemaExitOnlyGrid() {
  return scenarioRunFieldsSchema().pick([
    'name',
    'seniority',
    'pricePerShare',
    'impliedPricePerShare',
    'investedAmount',
    'liquidationProceeds',
    'commonProceeds',
    'cappedProceeds',
    'grossProceeds',
    'grossMoic',
    'moicCostBasis',
    'numberOfSharesOwned',
    'fullyDilutedShares',
    'ownershipPercentage',
  ]);
}

export const NoRunnableScenarioMessage =
  'This scenario has no data to run yet. Add your scenario details, then run it again.';

export const UnsavedScenarioResetTitle = 'UNSAVED SCENARIO CLEARED';
export const UnsavedScenarioResetMessage =
  'Scenario drafts are not saved across refreshes. Add your details again to run it.';

export function ScenarioVisualization() {
  const currentScenario = useAtomValue(currentScenarioStateJ);
  const allScenariosLocation = useAllScenariosLocation();
  const { search } = useLocation();
  const { pushWarningToast } = useToastMessageState();
  const [availableFields, setAvailableFields] = useState([] as IFormField<unknown>[]);
  const [scenarioData, setScenarioData] = useState<IScenarioRunResponseDataModel>();
  const [loading, setLoading] = useState(false);
  const schemaToGrid = useSchemaToGrid();
  const scenarioType = useMemo(() => {
    if (!currentScenario?.scenarioTransactions) {
      return null;
    } else if (currentScenario.scenarioTransactions.every((t) => t.type === ScenarioTransactionType.exit)) {
      return 'exitOnly';
    } else if (currentScenario.scenarioTransactions.every((t) => t.type === ScenarioTransactionType.round)) {
      return 'roundOnly';
    } else if (
      currentScenario.scenarioTransactions.every((t) => t.type === ScenarioTransactionType.partialExit)
    ) {
      return 'partialExit';
    } else if (
      currentScenario.scenarioTransactions.some((t) => t.type === ScenarioTransactionType.partialExit)
    ) {
      return 'roundAndPartialExit';
    } else {
      return 'roundAndExit';
    }
  }, [currentScenario]);

  const gridFieldsSchema = useMemo(() => {
    switch (scenarioType) {
      case 'exitOnly':
        return scenarioVisualizationSchemaExitOnlyGrid();
      case 'roundOnly':
        return scenarioVisualizationSchemaRoundOnlyGrid();
      case 'roundAndExit':
        return scenarioVisualizationSchemaRoundAndExitGrid();
      case 'partialExit':
        return scenarioVisualizationSchemaPartialExitOnlyGrid();
      case 'roundAndPartialExit':
        return scenarioVisualizationSchemaRoundAndPartialExitGrid();
      default:
        return yup.object();
    }
  }, [scenarioType]);

  const gridColdDefs = useMemo(() => {
    const defs = schemaToGrid(gridFieldsSchema);
    const moicCol = defs.find((def) => def.colId === 'grossMoic');
    if (moicCol) {
      moicCol.tooltipValueGetter = (params) => {
        const row = params.data as IScenarioRunDataModel | undefined;
        // Per-row rows carry the flag directly. The grand-total/aggregate row has no data, so scan the
        // contributing rows — the corrected aggregate MOIC is notional when any SAFE lot is.
        let isNotional = !!row?.isNotionalWarrantMoic;
        if (!row) {
          params.api?.forEachNode((node) => {
            if ((node.data as IScenarioRunDataModel | undefined)?.isNotionalWarrantMoic) {
              isNotional = true;
            }
          });
        }
        return isNotional ? NOTIONAL_WARRANT_MOIC_NOTE : null;
      };
    }
    return defs;
  }, [gridFieldsSchema, schemaToGrid]);

  const visualizationFieldsSchema = useMemo(() => {
    if (!currentScenario?.scenarioTransactions) {
      return yup.object();
    } else if (currentScenario.scenarioTransactions.every((t) => t.type === ScenarioTransactionType.exit)) {
      return scenarioVisualizationSchemaExitOnly();
    } else if (currentScenario.scenarioTransactions.every((t) => t.type === ScenarioTransactionType.round)) {
      return scenarioVisualizationSchemaRoundOnly();
    } else if (
      currentScenario.scenarioTransactions.some((t) => t.type === ScenarioTransactionType.partialExit)
    ) {
      return scenarioVisualizationSchemaPartialExit();
    } else {
      return scenarioVisualizationSchemaRoundAndExit();
    }
  }, [currentScenario]);

  useEffect(() => {
    if (!currentScenario || !isRunnableScenario(currentScenario)) {
      return;
    }
    setLoading(true);
    runScenario(prepRunData(currentScenario))
      .then((data) => {
        const runItem = data.rounds.at(-1);
        const fields = schemaToFormFields(visualizationFieldsSchema).filter((field) => {
          return field.dataType === 'number' && get(runItem, field.key) !== undefined;
        });

        setScenarioData(data);
        setAvailableFields(fields);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [visualizationFieldsSchema, currentScenario]);

  const kpiData = scenarioData?.rounds.at(-1);

  const settingsPath = allScenariosLocation
    ? `${allScenariosLocation}/${ROUTES.COMPANY_PROFILE_SINGLE_SCENARIO}/${ROUTES.COMPANY_PROFILE_SCENARIO_SETTINGS}`
    : null;

  // When we bounce an empty scenario back to the builder, tell the user why — once per mount.
  const resetToastShownRef = useRef(false);
  useEffect(() => {
    if (resetToastShownRef.current) {
      return;
    }
    const nothingToRun = !currentScenario || !scenarioType || !isRunnableScenario(currentScenario);
    if (nothingToRun && settingsPath) {
      resetToastShownRef.current = true;
      pushWarningToast({ title: UnsavedScenarioResetTitle, message: UnsavedScenarioResetMessage });
    }
  }, [currentScenario, scenarioType, settingsPath, pushWarningToast]);

  if (loading) {
    return <Loader />;
  }

  // Nothing to run (e.g. an unsaved scenario that was refreshed) — send the user
  // straight to the builder instead of a dead-end Results view.
  if (!currentScenario || !scenarioType || !isRunnableScenario(currentScenario)) {
    return settingsPath ? (
      <Navigate to={{ pathname: settingsPath, search }} replace />
    ) : (
      <NoDataMessage2 message={NoRunnableScenarioMessage} />
    );
  }

  return (
    <MainContainerWithAlert>
      <VisualizationHeading scenario={currentScenario} />
      {!scenarioData ? (
        <NoDataMessage2 />
      ) : (
        <>
          {kpiData && (
            <SubSection title={'Key Performance Indicators'} noCard={true}>
              <ScenarioKPIs fields={availableFields} lastRoundRunData={kpiData} />
            </SubSection>
          )}

          <SubSection
            title={'Investment Details'}
            noCard={true}
            collapseProps={{ collapsible: true, defaultExpanded: true }}
          >
            <ScenarioRunByFund
              colDefs={gridColdDefs}
              scenarioRunData={prepGridData(scenarioData.rounds)}
              initialClientFilterState={scenarioType !== 'roundOnly'}
              scenarioType={scenarioType}
            />
          </SubSection>

          <SubSection title={'KPIs By Share Class'} noCard={true}>
            <ScenarioCharts
              fields={prepChartFields(availableFields, scenarioType)}
              scenarioRunData={prepChartData(scenarioData.rounds)}
            />
          </SubSection>
        </>
      )}
    </MainContainerWithAlert>
  );
}

export function prepGridData(rounds: IScenarioRunDataModel[]) {
  return rounds.filter((r) => r.type === ScenarioTransactionType.round);
}

export function prepChartData(rounds: IScenarioRunDataModel[]) {
  return rounds.slice(0, -1);
}

// Post-money is a round-level figure: it belongs in the scenario KPIs, not the per-share-class charts.
// The round-only view also drops its Ownership Percentage chart.
export function prepChartFields(fields: IFormField<unknown>[], scenarioType?: ScenarioTypeByTransactions) {
  const excluded =
    scenarioType === 'roundOnly' ? ['postMoneyValuation', 'ownershipPercentage'] : ['postMoneyValuation'];
  return fields.filter((field) => !excluded.includes(field.key));
}

function prepRunData(scenario: Partial<IScenarioDataModel>) {
  const name = scenario.name || 'untitled';
  return { ...scenario, name };
}
